// Public state surface returned by useSimWorld.

import type { GameSnapshot, Ruleset, SeaState, Seat } from '@aod/protocol';
import { RIM_SEA } from '@aod/protocol';
import type { CitySuggestion, TickLoopRunState } from '@aod/sim-adapter';
import type { MinimapSnapshot, PlayerHudEntry } from '@aod/ui';
import type { Archetype, DoctrineState } from '../../ui/DoctrinePanel/index.js';
import type { WsConnectionState } from '../wsBridge.js';
import type { TreasurySummary } from './summarize.js';
import { type DynastyActions, NO_DYNASTY } from './useDynastyIntents.js';
import { type EspionageActions, NO_ESPIONAGE } from './useEspionageIntents.js';
import { type HeroCaptivityActions, NO_HERO_CAPTIVITY } from './useHeroCaptivityIntents.js';
import { type HeroQuestActions, NO_HERO_QUESTS } from './useHeroQuestIntents.js';
import { NO_TREATIES, type TreatyActions } from './useTreatyIntents.js';

export interface UseSimWorldOptions {
  readonly matchSeed?: number;
  readonly tickRateHz?: number;
  /** Which player slot to surface in the UI as "the player". Default 0. */
  readonly localPlayerIndex?: number;
  /**
   * Optional JSON string for GameConfig overrides.
   * Fetched from the persistent-world server's /api/config endpoint
   * when available. Falls back to the Rust-side Default implementation.
   */
  readonly configJson?: string;
  /**
   * Match ruleset chosen on the setup screen, forwarded to the wasm
   * constructor as `ruleset`. A new object boots a fresh match.
   */
  readonly ruleset?: Ruleset;
  /** Seats (seat 0 = local human), forwarded as `seats`. */
  readonly seats?: readonly Seat[];
  /** Team of each seat (multiplayer teams), forwarded as `teams`. */
  readonly teams?: readonly number[];
  /**
   * Authored `aod-map` document (JSON) from the World tab, forwarded to the
   * wasm constructor as `map`. A new object boots the authored match.
   */
  readonly mapJson?: string;
  /**
   * When true, the TickLoop is not created and the caller drives ticks
   * via externalStep(n). Default false.
   */
  readonly externalTick?: boolean;
}

export type VictoryPath = 'conquest' | 'economy' | 'science' | 'faith' | 'diplomacy' | 'milestones';

/** Match-end state. */
export interface VictoryState {
  readonly winner: number;
  readonly path: VictoryPath;
}

/** Per-player progress toward non-conquest victory paths. */
export interface PlayerVictoryProgress {
  readonly economyMetAt: number;
  readonly scienceMetAt: number;
  readonly faithMetAt: number;
  readonly diplomacyMetAt: number;
}

/** Thresholds for each victory path (from VictoryConfig). 0 = disabled. */
export interface VictoryConfigState {
  readonly minEmpireTick: number;
  readonly sustainEmpireTicks: number;
  readonly economyGold: number;
  readonly economyTradeRoutes: number;
  readonly scienceKnowledge: number;
  readonly faithTotal: number;
  readonly faithTemples: number;
  readonly diplomacyHonor: number;
}

/** Live victory progress data surfaced from the sim. */
export interface VictoryProgressState {
  readonly progress: Record<number, PlayerVictoryProgress>;
  readonly config: VictoryConfigState;
  readonly empireTick: number;
}

export interface SimWorld {
  readonly status: 'loading' | 'ready' | 'error';
  readonly error?: string;
  readonly snapshot: GameSnapshot | null;
  readonly tick: bigint;
  readonly tileCount: number;
  readonly cityCount: number;
  readonly determinismHash: bigint;
  readonly wsState: WsConnectionState;
  readonly localPlayer: DoctrineState | null;
  readonly localTreasury: TreasurySummary;
  /** Per-player HUD data (power scores, colors, info levels). */
  readonly playerHudEntries: PlayerHudEntry[];
  /** Minimap snapshot built from current tick's scene. */
  readonly minimapSnapshot: MinimapSnapshot | null;
  readonly invest: (archetype: Archetype) => void;

  // -------- time controls (Phase 1 milestone B) -------------------------
  /** 'running' | 'paused' | 'stopped' — derived from the underlying TickLoop. */
  readonly runState: TickLoopRunState;
  /** Effective tick-rate multiplier. 1× nominal | 2× | 4×. */
  readonly speed: number;
  readonly pause: () => void;
  readonly resume: () => void;
  readonly setSpeed: (multiplier: number) => void;
  /** Test/dev: step N ticks in WASM batches, publish once (see useSimWorld). */
  readonly fastForward: (ticks: number) => void;

  // -------- conquest (Phase 1 milestone C) ------------------------------
  /** Set once a player wins; `null` while the match is live. */
  readonly victory: VictoryState | null;
  /** Live per-player victory progress for non-conquest paths. */
  readonly victoryProgress: VictoryProgressState | null;
  /** Fire an attack against the given city node id. No-op if invalid. */
  readonly attackCity: (targetCityId: string) => void;

  // -------- new match (Phase 1 milestone D) -----------------------------
  /**
   * Re-seed and rebuild the demo match. If `newSeed` is omitted, a random
   * seed is generated. Resumes the loop and clears `victory`.
   */
  readonly newMatch: (newSeed?: number) => void;

  // -------- units (Phase 2 milestone G) ----------------------------------
  /** Move the player's unit to an adjacent hex. Returns true if intent accepted. */
  readonly moveUnit: (unitId: string, targetQ: number, targetR: number) => boolean;
  /** Engineer builds a bridge across the river edge toward the target tile. */
  readonly buildBridge: (unitId: string, targetQ: number, targetR: number) => boolean;
  /** Plot a course for a ship and let it sail there (the `sea.trade` module). */
  readonly sailShip: (unitId: string, targetQ: number, targetR: number) => boolean;
  /** Work a harbour: load at your own port, sell at a foreign one. */
  readonly dockTrade: (unitId: string, cityId: string, food?: string) => boolean;
  /** Take the offer a foreign house has made to one of your harbours. */
  readonly acceptMerchant: (cityId: string) => boolean;
  /** Refuse it. */
  readonly declineMerchant: (cityId: string) => boolean;
  /** Marriages between allied houses (the `diplomacy.dynasty` module). */
  readonly dynasty: DynastyActions;
  /** Spies, rescues and ransoms (the `espionage.enabled` module). */
  readonly espionage: EspionageActions;
  /** The captor's choice, and the ransom for a captive hero
   *  (the `heroes.captivity` module). */
  readonly heroCaptivity: HeroCaptivityActions;
  /** Giving a hero an order (the `heroes.quests` module). */
  readonly heroQuests: HeroQuestActions;
  /** Peace, pacts and alliances (the `diplomacy.basic` module). */
  readonly treaty: TreatyActions;
  /** Deploy a garrisoned unit to an adjacent hex. Returns true if intent accepted. */
  readonly deployUnit: (unitId: string, targetQ: number, targetR: number) => boolean;
  /** Set a unit's battle formation. */
  readonly setFormation: (unitId: string, formation: string) => void;
  /** Start a research project (spends knowledge for empire-wide bonuses). */
  readonly startResearch: () => void;
  /** Active research state, or null if no research is running. */
  readonly researchState: {
    active: boolean;
    remainingTicks: number;
    industryBonus: number;
    knowledgeBonus: number;
  } | null;
  /** Faith crisis state. Null if no tracker exists yet. */
  readonly faithCrisis: {
    tension: number;
    level: string;
    firedEvents: readonly string[];
  } | null;
  /** Perform pilgrimage to reduce faith crisis tension (costs gold). */
  readonly pilgrimage: () => void;
  /** Enact religious reform to reduce faith crisis tension (costs faith). */
  readonly reform: () => void;

  // -------- buildings -------------------------------------------------------
  /** Start constructing a building in a city. Returns true if intent accepted. */
  readonly startBuilding: (cityId: string, buildingId: string) => boolean;
  /**
   * Specialize an owned city into an archetype from the local seat's
   * alignment path. Returns true if the intent was accepted (false when
   * rejected: not the owner, off-path without an artifact, …).
   */
  readonly setCityDoctrine: (cityId: string, archetype: Archetype) => boolean;
  /**
   * Top city build/train suggestions for the local seat (spec §8
   * `suggest` auto-manage mode): up to 2 intents, never executed.
   * Empty unless that seat's `autoManageCities` is `"suggest"`.
   */
  readonly suggestIntents: () => readonly CitySuggestion[];

  // -------- champions (legends.enabled) ---------------------------------------
  /** Start the Champion summon project in a city. Null = accepted, else the sim's rejection code. */
  readonly summonLegend: (cityId: string) => string | null;
  /** Cast a Champion ability at a hex. Null = accepted, else the sim's rejection code. */
  readonly activateLegendAbility: (
    unitId: string,
    slot: number,
    targetQ: number,
    targetR: number,
  ) => string | null;

  // -------- heroes -----------------------------------------------------------
  /** Activate a hero ability in the given slot. */
  readonly activateHeroAbility: (unitId: string, slotIndex: number) => void;
  /** Select a new ability for a hero (consumes an ability pick). */
  readonly selectHeroAbility: (unitId: string, slotIndex: number, abilityId: string) => void;

  // -------- events ----------------------------------------------------------
  /** Map climate (terrain-levels D11) — pins the season for arctic/tropical. */
  readonly climate: 'temperate' | 'arctic' | 'tropical' | 'desert';
  /** The sea axis (sea-design §3 A2) — the mode and the board's frame. */
  readonly sea: SeaState;

  /** Accumulated game events since the match started. */
  readonly events: ReadonlyArray<{ id: number; type: string; payload?: Record<string, unknown> }>;

  // -------- save / load -----------------------------------------------------
  /** Serialize full game state to JSON string for download. */
  readonly saveGame: () => string;
  /** Restore full game state from a JSON string produced by `saveGame`. */
  readonly loadGame: (json: string) => void;

  // -------- external tick mode (server-authoritative lockstep) -------------
  /**
   * Step the sim externally (server-authoritative mode).
   * Only valid when `externalTick: true` was passed to useSimWorld options.
   * The caller is responsible for calling publish after each step.
   */
  readonly externalStep: (tick: number) => void;
  /**
   * Apply a server-confirmed frame of intents to the sim.
   * Used in server-authoritative mode to reconcile optimistic intent
   * execution with the authoritative tick state.
   */
  readonly applyFrame: (frame: Record<string, readonly unknown[]>) => void;
  /** Get the current determinism hash from the WASM adapter. */
  readonly getDeterminismHash: () => bigint;
}

export const PLACEHOLDER_SIM_WORLD: SimWorld = {
  status: 'loading',
  climate: 'temperate',
  sea: RIM_SEA,
  snapshot: null,
  tick: 0n,
  tileCount: 0,
  cityCount: 0,
  determinismHash: 0n,
  wsState: 'closed',
  localPlayer: null,
  localTreasury: { food: 0, industry: 0, knowledge: 0, faith: 0, gold: 0 },
  playerHudEntries: [],
  minimapSnapshot: null,
  invest: () => {},
  runState: 'stopped',
  speed: 1,
  pause: () => {},
  resume: () => {},
  setSpeed: () => {},
  fastForward: () => {},
  victory: null,
  victoryProgress: null,
  attackCity: () => {},
  newMatch: () => {},
  moveUnit: () => false,
  buildBridge: () => false,
  deployUnit: () => false,
  setFormation: () => {},
  startResearch: () => {},
  researchState: null,
  faithCrisis: null,
  pilgrimage: () => {},
  reform: () => {},
  startBuilding: () => false,
  sailShip: () => false,
  dockTrade: () => false,
  acceptMerchant: () => false,
  declineMerchant: () => false,
  dynasty: NO_DYNASTY,
  espionage: NO_ESPIONAGE,
  heroCaptivity: NO_HERO_CAPTIVITY,
  heroQuests: NO_HERO_QUESTS,
  treaty: NO_TREATIES,
  setCityDoctrine: () => false,
  suggestIntents: () => [],
  summonLegend: () => 'unavailable',
  activateLegendAbility: () => 'unavailable',
  activateHeroAbility: () => {},
  selectHeroAbility: () => {},
  events: [],
  saveGame: () => '',
  loadGame: () => {},
  externalStep: () => {},
  applyFrame: () => {},
  getDeterminismHash: () => 0n,
};
